import { useEffect, useRef, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { NotificationService } from '../../services/notification'
import { ApiVersion } from '../../enums/api-version.enum'
import { GetNotificationResponse } from '@pushly/models/lib/structs/notifications/get-requests/get-notification-response'

interface UseNotificationDetailOptions {
    /** When false, skips the fetch (useful for conditional rendering like bottom sheets) */
    enabled?: boolean
    /** Cancellation key prefix for deduplication */
    cancellationKey?: string
}

interface UseNotificationDetailResult {
    /** The fetched notification object, or null if not yet loaded / not found */
    data: GetNotificationResponse | null
    /** Whether the fetch is in progress */
    loading: boolean
}

/**
 * Fetches a single notification by ID with includeSegments.
 * Handles cancellation on unmount and re-fetches when domainId/notificationId change.
 *
 * Used by: NotificationDetail, NotificationQuickSheet
 */
export function useNotificationDetail(
    domainId: string | number | null | undefined,
    notificationId: string | number | null | undefined,
    options?: UseNotificationDetailOptions,
): UseNotificationDetailResult {
    const enabled = options?.enabled !== false
    const cancellationKey = options?.cancellationKey ?? 'mv.notif-detail.fetch'
    const notificationService = useRef(Container.get(NotificationService)).current

    const [data, setData] = useState<GetNotificationResponse | null>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!enabled || !domainId || !notificationId) {
            setData(null)
            return
        }

        let cancelled = false
        setLoading(true)

        const fetchNotif = async () => {
            try {
                const result = await notificationService.fetchNotificationById(
                    Number(domainId),
                    String(notificationId),
                    {
                        query: { includeSegments: true },
                        cancellationKey,
                        version: ApiVersion.V4,
                    },
                )
                if (!cancelled && result) {
                    setData(GetNotificationResponse.parseJsonObject(result))
                }
            } catch {
                // Caller handles null data as not-found / error state
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        fetchNotif()

        return () => {
            cancelled = true
        }
    }, [enabled, domainId, notificationId, notificationService, cancellationKey])

    return { data, loading }
}
